/**
 * Hand-drawn signature pad, as an alternative to uploading a PNG.
 *
 * Whichever signature image actually gets used to sign -- drawn on this pad,
 * or uploaded via the file input -- can optionally be kept in the browser
 * (IndexedDB, see idb.ts) so it doesn't have to be redrawn/re-uploaded next
 * time, surviving reloads and full browser restarts the same way the
 * certificate Komfort-Login in crypto/keystore.ts does.
 */

import { runTransaction } from "./idb";

const SIGNATURE_STORE = "signature";
const RECORD_ID = "default";

export interface SavedSignature {
  bytes: ArrayBuffer;
  mimeType: string;
  savedAt: number;
}

export interface SignaturePad {
  clear(): void;
  isEmpty(): boolean;
  toBlob(): Promise<Blob>;
  /** Marks the pad as non-empty without drawing through it -- used after
   * restoring a saved signature directly onto the canvas. */
  markNonEmpty(): void;
  /** Marks the pad as empty *without* clearing the canvas pixels -- used when
   * the canvas holds a live preview of an uploaded file rather than genuine
   * hand-drawn strokes, so `isEmpty()` (which submit-time code uses to
   * decide between the pad's own re-rasterized snapshot and the pristine
   * uploaded bytes) still reflects "nothing was actually drawn here". */
  markEmpty(): void;
}

/** `onEmptyChange` fires whenever the pad transitions between empty and
 * non-empty (drawing, clearing, or `markNonEmpty()`), so callers can show or
 * hide the upload/draw controls without polling `isEmpty()`. */
export function attachSignaturePad(
  canvas: HTMLCanvasElement,
  onEmptyChange?: (empty: boolean) => void,
): SignaturePad {
  const ctx = canvas.getContext("2d")!;
  let hasDrawn = false;
  let drawing = false;
  let last: { x: number; y: number } | null = null;

  function setHasDrawn(value: boolean): void {
    if (hasDrawn === value) return;
    hasDrawn = value;
    onEmptyChange?.(!hasDrawn);
  }

  function clearCanvas(): void {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  }
  clearCanvas();

  function canvasPoint(event: PointerEvent): { x: number; y: number } {
    const bounds = canvas.getBoundingClientRect();
    return {
      x: (event.clientX - bounds.left) * (canvas.width / bounds.width),
      y: (event.clientY - bounds.top) * (canvas.height / bounds.height),
    };
  }

  function onPointerDown(event: PointerEvent): void {
    canvas.setPointerCapture(event.pointerId);
    drawing = true;
    last = canvasPoint(event);
    event.preventDefault();
  }

  function onPointerMove(event: PointerEvent): void {
    if (!drawing || !last) return;
    const point = canvasPoint(event);
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    last = point;
    setHasDrawn(true);
    event.preventDefault();
  }

  function onPointerUp(event: PointerEvent): void {
    drawing = false;
    last = null;
    event.preventDefault();
  }

  canvas.addEventListener("pointerdown", onPointerDown);
  canvas.addEventListener("pointermove", onPointerMove);
  canvas.addEventListener("pointerup", onPointerUp);
  canvas.addEventListener("pointerleave", onPointerUp);

  return {
    clear: clearCanvas,
    isEmpty: () => !hasDrawn,
    markNonEmpty: () => setHasDrawn(true),
    markEmpty: () => setHasDrawn(false),
    toBlob: () =>
      new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error("could not encode signature as PNG"));
        }, "image/png");
      }),
  };
}

export async function saveSignature(blob: Blob): Promise<void> {
  const bytes = await blob.arrayBuffer();
  await runTransaction(SIGNATURE_STORE, "readwrite", (store) =>
    store.put(
      { bytes, mimeType: blob.type || "image/png", savedAt: Date.now() } satisfies SavedSignature,
      RECORD_ID,
    ),
  );
}

export async function loadSavedSignature(): Promise<Blob | null> {
  const saved = await runTransaction<SavedSignature | undefined>(SIGNATURE_STORE, "readonly", (store) =>
    store.get(RECORD_ID),
  );
  if (!saved) return null;
  return new Blob([saved.bytes], { type: saved.mimeType });
}

export async function clearSavedSignature(): Promise<void> {
  await runTransaction(SIGNATURE_STORE, "readwrite", (store) => store.delete(RECORD_ID));
}

/** Draws `blob` onto `canvas`, fitting it within the canvas preserving
 * aspect ratio rather than stretching to fill: a drawn signature is already
 * exactly the canvas's own size (no-op either way), but an uploaded PNG can
 * be any aspect ratio and stretching it would visibly distort it. Uses
 * createImageBitmap rather than an <img data:...> element, so no
 * img-src/data: CSP allowance is needed. */
export async function drawBlobFitted(canvas: HTMLCanvasElement, blob: Blob): Promise<void> {
  const bitmap = await createImageBitmap(blob);
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  const scale = Math.min(canvas.width / bitmap.width, canvas.height / bitmap.height);
  const width = bitmap.width * scale;
  const height = bitmap.height * scale;
  ctx.drawImage(bitmap, (canvas.width - width) / 2, (canvas.height - height) / 2, width, height);
}

/** Restores a previously saved signature onto `canvas`, if one exists. */
export async function restoreSavedSignature(canvas: HTMLCanvasElement): Promise<boolean> {
  const blob = await loadSavedSignature();
  if (!blob) return false;

  await drawBlobFitted(canvas, blob);
  return true;
}
